import { ListFilter, PanelsTopLeft, UserRoundPlus } from "lucide-react";
import { Button } from "../ui/button";
import { SearchInput } from "../ui/search-input";
import { FilterState, ProjectLabel, ProjectUser } from "./types";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { IconUser } from "../ui/icons";
import { cn } from "@/lib/utils";
import { UserDropdown } from "./UserDropdown";
import * as Checkbox from "@radix-ui/react-checkbox";
import { LabelDropdown } from "./LabelDropdown";
import { InviteModal } from "./InviteModal";

interface Props {
  filters: FilterState;
  setFilters: Dispatch<SetStateAction<FilterState>>;
  project: any;
}

interface UserFilterProps {
  users: Array<ProjectUser>;
  selectedUsers: Array<string>;
  onChange: (ids: Array<string>) => void;
}

function UsersFilter({ users, selectedUsers, onChange }: UserFilterProps) {
  const [usersToShow, setUsersToShow] = useState<Array<ProjectUser>>(
    users?.slice(0, 2)
  );

  //Sort the array to show the selected avatars first
  useEffect(() => {
    if (selectedUsers.length) {
      const sortedArray = users?.sort((a, b) => {
        const indexA = selectedUsers.indexOf(a.id);
        const indexB = selectedUsers.indexOf(b.id);

        if (indexA === -1 && indexB === -1) {
          return 0;
        } else if (indexA === -1) {
          return 1;
        } else if (indexB === -1) {
          return -1;
        } else {
          return indexA - indexB;
        }
      });
      setUsersToShow(sortedArray?.slice(0, 2) || []);
    } else {
      setUsersToShow(users?.slice(0, 2) || []);
    }
  }, [selectedUsers, users]);

  const renderUserIcon = useCallback(
    (user: ProjectUser) => {
      return user.picture ? (
        <img
          src={user.picture}
          alt={user.name}
          className={cn(
            "size-6 border-2 border-secondary-n-00 shadow-md rounded-md bg-avatar",
            selectedUsers.includes(user.id) ? "border-primary-n-600" : undefined
          )}
          crossOrigin="anonymous"
        />
      ) : (
        <div
          className={cn(
            "flex bg-secondary-n-1000 items-center justify-center size-6 rounded-md border-2 border-secondary-n-600",
            selectedUsers.includes(user.id) ? "border-primary-n-600" : undefined
          )}
        >
          <IconUser />
        </div>
      );
    },
    [selectedUsers]
  );

  const dropdownTrigger = useMemo(
    () => (
      <div className="flex gap-1 items-center cursor-pointer">
        <div className="flex">
          {usersToShow?.map((user) => (
            <div
              key={user?.id}
              title={user?.name ?? user?.email}
              className={
                usersToShow.length > 1
                  ? "last:-translate-x-1 first:z-0 last:z-10 shadow-md"
                  : undefined
              }
            >
              {renderUserIcon(user)}
            </div>
          ))}
        </div>
        {users?.length > 2 && (
          <span
            className={cn(
              "p-0.5 text-secondary-n-600 text-xs font-medium rounded-md border-2 border-transparent",
              selectedUsers.length > 2
                ? "bg-primary-n-900 bg-opacity-25 text-secondary-n-400"
                : undefined
            )}
          >
            +{users.length - 2}
          </span>
        )}
      </div>
    ),
    [usersToShow, selectedUsers]
  );

  return (
    <UserDropdown
      triggerElement={dropdownTrigger}
      users={users}
      onChange={onChange}
      title="Filter by user"
      multiple
      clearString="Clear user filter"
    />
  );
}

interface LabelFiltersProps {
  labels: Array<ProjectLabel>;
  selectedLabels: Array<string>;
  onChange: (ids: Array<string>) => void;
}

function LabelsFilter({ labels, selectedLabels, onChange }: LabelFiltersProps) {
  const filterTriggerButton = useMemo(() => {
    const selectedLabelsObj =
      labels?.filter((label: any) => selectedLabels?.includes(label.id)) || [];
    return (
      <div className="flex gap-2 cursor-pointer">
        {selectedLabelsObj?.length === 1 && (
          <div
            key={selectedLabelsObj[0].id}
            className="flex gap-2 items-center text-secondary-n-400 rounded-lg bg-secondary-n-950 px-2"
          >
            {selectedLabelsObj[0].color && (
              <Checkbox.Root
                className="w-2.5 h-2.5 border rounded-xs flex items-center justify-center"
                style={{ borderColor: selectedLabelsObj[0].color.value }}
                defaultChecked
              >
                <Checkbox.Indicator
                  className="w-full h-full flex items-center justify-center"
                  style={{ backgroundColor: selectedLabelsObj[0].color.value }}
                ></Checkbox.Indicator>
              </Checkbox.Root>
            )}
            <span className="text-xs">{selectedLabelsObj[0].label}</span>
          </div>
        )}
        {selectedLabelsObj.length > 1 && (
          <div className="flex gap-2 items-center text-secondary-n-400 rounded-lg bg-secondary-n-950 px-2">
            {selectedLabelsObj.slice(0, 3).map(
              (label: any, index: number) =>
                label.color && (
                  <Checkbox.Root
                    key={label.id}
                    className={`w-2.5 h-2.5 border rounded-xs flex items-center justify-center ${
                      index > 0 ? "-ml-1.5" : ""
                    }`}
                    style={{ borderColor: label?.color?.value || "#000" }}
                    defaultChecked
                  >
                    <Checkbox.Indicator
                      className="w-full h-full flex items-center justify-center"
                      style={{ backgroundColor: label?.color?.value || "#000" }}
                    ></Checkbox.Indicator>
                  </Checkbox.Root>
                )
            )}
            <span className="text-xs">{selectedLabelsObj.length} labels</span>
          </div>
        )}
        <Button
          className={cn(
            "h-7 flex items-center gap-1",
            selectedLabels.length && "px-1"
          )}
          variant="tertiary"
        >
          <ListFilter className="size-4" />
          {!selectedLabels.length && <p className="text-xs">Filter</p>}
        </Button>
      </div>
    );
  }, [selectedLabels, labels]);

  return (
    <LabelDropdown
      triggerElement={filterTriggerButton}
      labels={labels}
      title="Filter by label"
      clearString="Clear label filter"
      onChange={onChange}
      addNew={false}
    />
  );
}

export function ProjectPageHeader({ filters, setFilters, project }: Props) {
  const [showInviteModal, setShowInviteModal] = useState(false);

  const handleSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFilters((prev: FilterState) => {
        return {
          ...prev,
          search: event?.target?.value as string,
        } as FilterState;
      });
    },
    [setFilters]
  );

  const handleUserFilterChange = useCallback(
    (userIds: Array<string>) => {
      setFilters((prev: FilterState) => {
        return {
          ...prev,
          userIds,
        } as FilterState;
      });
    },
    [setFilters]
  );

  const handleLabelsFilterChange = useCallback(
    (labels: Array<string>) => {
      setFilters((prev: FilterState) => {
        return {
          ...prev,
          labels,
        } as FilterState;
      });
    },
    [setFilters]
  );

  return (
    <div className="flex items-center justify-between border-b border-secondary-n-900 mx-4 mb-4 pb-4 min-w-0">
      <div className="flex items-center gap-3 text-secondary-n-400">
        <PanelsTopLeft className="size-4" />
        <a
          // @ts-expect-error TS2322
          href={project ? `https://github.com/${project?.repoPath}` : null}
          target="_blank"
          className={cn(
            "text-xs cursor-pointer",
            project ? undefined : "hidden"
          )}
        >
          {project?.repoPath}
        </a>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-40">
          <SearchInput onChange={handleSearch} />
        </div>
        <LabelsFilter
          labels={project?.labels}
          onChange={handleLabelsFilterChange}
          selectedLabels={filters.labels}
        />
        <UsersFilter
          users={project?.users}
          onChange={handleUserFilterChange}
          selectedUsers={filters.userIds}
        />
        <div className="border-r h-7 border-secondary-n-900">{""}</div>
        <Button
          onClick={() => setShowInviteModal(true)}
          className="h-7 flex items-center gap-1"
          variant="secondary"
        >
          <UserRoundPlus className="size-4" />
          <p className="text-xs">Invite</p>
        </Button>
      </div>
      <InviteModal
        open={showInviteModal}
        setOpen={setShowInviteModal}
        projectId={project?.id}
      />
    </div>
  );
}
